import { SmilePlus } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import type { TeamChatReaction } from '../../lib/teamChatApi';
import { Popover } from '../tasks/linear/Popover';
import { Tooltip } from '../ui/Tooltip';
import { EmojiPicker } from './EmojiPicker';

/** Past this many names the tooltip counts the rest instead of listing them. */
const MAX_NAMES = 3;

/**
 * Discord-style reaction pills under a message. Clicking a pill toggles the
 * viewer's reaction; the trailing `+` opens the emoji picker.
 *
 * Hovering a pill says who reacted, through the app's own {@link Tooltip}. It
 * used to be the native `title` attribute, which the browser renders in OS
 * chrome after about a second — unstyled on a dark UI, and slow enough that it
 * read as nothing happening at all.
 */
export function ReactionBar({
  reactions,
  onToggle,
}: {
  reactions: TeamChatReaction[];
  onToggle: (emoji: string) => void;
}) {
  const { t } = useI18n();
  if (reactions.length === 0) return null;

  /** "Ana", "Ana și Bogdan", "Ana, Bogdan și Cristi", "Ana, Bogdan și încă 4". */
  function formatNames(names: string[]): string {
    if (names.length <= MAX_NAMES) {
      if (names.length <= 1) return names[0] ?? '';
      return t('teamChat.reaction.nameList', {
        names: names.slice(0, -1).join(', '),
        last: names[names.length - 1],
      });
    }
    return t('teamChat.reaction.nameListMore', {
      names: names.slice(0, MAX_NAMES).join(', '),
      count: names.length - MAX_NAMES,
    });
  }

  function tooltipFor(reaction: TeamChatReaction) {
    // The server sends the names it knows; if it ever sends none, fall back to
    // the count rather than rendering "  a reacționat cu 🍀".
    if (reaction.userNames.length === 0) return null;
    const key =
      reaction.userNames.length === 1
        ? 'teamChat.reaction.tooltipOne'
        : 'teamChat.reaction.tooltipMany';
    return t(key, { names: formatNames(reaction.userNames), emoji: reaction.emoji });
  }

  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      {reactions.map((reaction) => (
        <Tooltip key={reaction.emoji} label={tooltipFor(reaction)}>
          <button
            type="button"
            onClick={() => onToggle(reaction.emoji)}
            aria-pressed={reaction.mine}
            className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-colors ${
              reaction.mine
                ? 'border-brand-600 bg-brand-600/10 text-brand-600'
                : 'border-border bg-accent/50 text-muted-foreground hover:border-brand-600/50'
            }`}
          >
            <span className="text-sm leading-none">{reaction.emoji}</span>
            <span className="font-medium">{reaction.count}</span>
          </button>
        </Tooltip>
      ))}
      <Popover
        width={280}
        trigger={({ toggle, ref }) => (
          // Same tooltip as the pills beside it: one native and one styled
          // tooltip in the same row is worse than either on its own.
          <Tooltip label={t('teamChat.addReaction')}>
            <button
              type="button"
              ref={(el) => ref(el)}
              onClick={toggle}
              aria-label={t('teamChat.addReaction')}
              className="flex h-6 w-6 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:border-brand-600/50 hover:text-foreground"
            >
              <SmilePlus size={13} />
            </button>
          </Tooltip>
        )}
      >
        {(close) => (
          <EmojiPicker
            onPick={(emoji) => {
              onToggle(emoji);
              close();
            }}
          />
        )}
      </Popover>
    </div>
  );
}
